import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { CreateUpgradeInput } from '__generated__/graphql';
import { Promotion, Ticket, getVisibleProductsForTicketPromotion } from '../helpers';
import { SectionTitle } from '../../Common/Layout';
import { calculateQuantities, calculateUpgradeAvailability } from '../useQuantities';
import { useEvent } from '../EventProvider';
import CheckoutContext from '../CheckoutContext';
import ProductSelector from '../Personalisation/ProductSelector';
import UI from '../../../common/UI';

export interface BusinessProductSelectorProps {
  ticket: Ticket;
  promotion: Promotion;
}

const BusinessProductSelector = ({ ticket, promotion }: BusinessProductSelectorProps) => {
  const { t } = useTranslation();
  const { event } = useEvent();

  const {
    form,
    setBusinessRegistrations,
    quantities,
    availability,
  } = useContext(CheckoutContext);

  /**
   * The products that can be chosen with the current ticket.
   * Product variants are removed, because they are chosen when claiming a ticket.
   */
  const products = getVisibleProductsForTicketPromotion(promotion, event.products_for_sale)
    .map((product) => ({
      ...product,
      active_product_variants: [],
    }));

  /**
   * Sets the upgrades on all business registrations with the current ticket.
   */
  const handleProductsChange = useCallback(
    (upgrades: CreateUpgradeInput[]) => {
      setBusinessRegistrations(
        (registrations) => registrations.map((registration) => {
          if (promotion.id === registration.promotion.id) {
            return {
              ...registration,
              upgrades,
            };
          }

          return registration;
        }),
        event,
      );
    },
    [setBusinessRegistrations, promotion, event],
  );

  /** All business registrations with the same ticket promotion. */
  const businessRegistrations = form.registrations.business.filter((registration) => (
    promotion.id === registration.promotion.id
  ));

  /** The selected quantities of this ticket's upgrades. */
  const upgradeQuantities = calculateQuantities({
    businessRegistrations,
    ticketCategories: event.ticket_categories,
    products,
  });

  /** The total quantity for the current ticket. */
  const ticketQuantity = businessRegistrations.reduce((quantity, registration) => (
    quantity + registration.quantity
  ), 0);

  /** The units in which upgrades are bought. */
  const units = ticketQuantity * ticket.units;

  /** The remaining availability of this ticket's products.  */
  const upgradeAvailability = calculateUpgradeAvailability({
    quantities: upgradeQuantities,
    availability,
    products,
    units,
  });

  return (
    <UI.FormGrid>
      <SectionTitle>
        {t('extras_form.extras_for')}
        {' '}
        <UI.Span style={{ fontWeight: 500 }}>
          <UI.Delimit>
            <UI.Span>
              <UI.Icon>
                <UI.Icons.Ticket />
              </UI.Icon>
              {' '}
              {ticket.title}
            </UI.Span>
            {promotion.title}
          </UI.Delimit>
        </UI.Span>
      </SectionTitle>
      <ProductSelector
        event={event}
        products={products}
        upgrades={businessRegistrations[0].upgrades}
        onChange={handleProductsChange}
        quantities={upgradeQuantities}
        globalQuantities={quantities}
        availability={upgradeAvailability}
        units={units}
        showVat
      />
    </UI.FormGrid>
  );
};

export default BusinessProductSelector;
